/**
 * ActivityChips component - the collapsed Activity summary shown once an
 * assistant response has arrived.
 *
 * Renders exactly one chip per associated activity, ordered ascending by start
 * time (ties broken by receipt order, via {@link orderActivities}). Each chip is
 * labeled by its activity type through {@link getActivityPresentation}, so the
 * type is always conveyed by visible text — never by color alone (Requirement
 * 13.7). A small type icon accompanies each label as a decorative supplement;
 * because the label text already names the type, the icon is marked
 * `aria-hidden` and the visible label is its text alternative.
 *
 * Requirements: 8.1 (one entry per activity, ascending start order), 8.2
 * (label determined by activity type, applied consistently), 8.3 (caller renders
 * nothing when there are no activities — this component expects a non-empty list
 * but also degrades to nothing when given an empty one), 13.7 (type conveyed by
 * text, not color alone).
 */
import { clsx } from 'clsx';
import type { Activity } from '@/types/chat';
import {
  getActivityPresentation,
  orderActivities,
  type ActivityIconName,
} from './activityPresentation';

export interface ActivityChipsProps {
  /** The message's Activity_Trace. One chip is rendered per activity. */
  activities: Activity[];
}

/**
 * Resolve a stable icon identifier to a small decorative SVG. Icons are marked
 * `aria-hidden` by the chip because the visible label already conveys the type.
 */
function ActivityIcon({ icon }: { icon: ActivityIconName }) {
  const common = {
    className: 'h-3.5 w-3.5 flex-shrink-0',
    viewBox: '0 0 20 20',
    fill: 'currentColor' as const,
    'aria-hidden': true as const,
  };

  switch (icon) {
    case 'memory':
      // Clock / recall
      return (
        <svg {...common}>
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm.75-13a.75.75 0 00-1.5 0v5c0 .3.18.57.46.69l3 1.25a.75.75 0 00.58-1.38l-2.54-1.06V5z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'knowledge-base':
      // Book
      return (
        <svg {...common}>
          <path d="M3 4.75A1.75 1.75 0 014.75 3H9.5a.75.75 0 01.75.75v12.5a.75.75 0 01-1.19.61A4.23 4.23 0 006.5 16H4.75A1.75 1.75 0 013 14.25V4.75zM11.5 3.75A.75.75 0 0112.25 3h3A1.75 1.75 0 0117 4.75v9.5A1.75 1.75 0 0115.25 16H13.5c-.98 0-1.9.33-2.56.86a.75.75 0 01-1.19-.61V3.75z" />
        </svg>
      );
    case 'tool':
      // Wrench
      return (
        <svg {...common}>
          <path
            fillRule="evenodd"
            d="M14.5 2a4.5 4.5 0 00-4.17 6.2L2.6 15.93a1.5 1.5 0 002.12 2.12l7.73-7.73A4.5 4.5 0 1014.5 2zm0 2a2.5 2.5 0 11-.9 4.83.75.75 0 00-.8.17l-8.1 8.1a.5.5 0 01-.7-.7l8.1-8.1a.75.75 0 00.17-.8A2.5 2.5 0 0114.5 4z"
            clipRule="evenodd"
          />
        </svg>
      );
    case 'generic':
    default:
      // Dot / sparkle
      return (
        <svg {...common}>
          <path
            fillRule="evenodd"
            d="M10 2a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5A.75.75 0 0110 2zm0 13.25a.75.75 0 01.75.75v1.5a.75.75 0 01-1.5 0v-1.5a.75.75 0 01.75-.75zM4 10a.75.75 0 01-.75.75h-1.5a.75.75 0 010-1.5h1.5A.75.75 0 014 10zm13.25-.75a.75.75 0 010 1.5h-1.5a.75.75 0 010-1.5h1.5zM10 6.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7z"
            clipRule="evenodd"
          />
        </svg>
      );
  }
}

export function ActivityChips({ activities }: ActivityChipsProps) {
  if (activities.length === 0) {
    return null;
  }

  const ordered = orderActivities(activities);

  return (
    <ul
      className="flex flex-wrap items-center gap-1.5"
      aria-label="Activity summary"
      data-testid="activity-chips"
    >
      {ordered.map((activity) => {
        const { label, icon } = getActivityPresentation(activity);
        return (
          <li key={activity.id}>
            <span
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5',
                'bg-dark-700/70 text-xs font-medium text-dark-200',
                'border border-dark-600/40'
              )}
              data-testid="activity-chip"
            >
              <ActivityIcon icon={icon} />
              <span>{label}</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}
